import React, { useEffect, useState } from 'react';
import './style.less';
import { Drawer, Tabs } from 'antd';
import { ConnectedCallDetailHeader } from './ConnectedCallDetailHeader';
import { ConnectedCallDetailProfile } from './ConnectedCallDetailProfile';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'shared/store/reducers';
import { selectCallDispositions, setDetailOpen } from 'shared/store/outbound-slice';
import { ConnectedCallDetailActivity } from './ConnectedCallDetailActivity';
import { ConnectedCallLog } from './ConnectedCallLog';
import { ConnectedCallDetailFooter } from './ConnectedCallDetailFooter';
import { CallTask, CallTaskStatus, CompletedCallTaskStates } from 'types/outbound';
import { DispositionModal } from './DispositionModal';
import { useAppSelector } from 'shared/store';
import { ConnectedCallDetailTranscript } from './Transcript';

export const ConnectedCallDetail: React.FC<{
  isConnected: boolean;
  isDialing: boolean;
  isOpen: boolean;
  onCallNextClick: (task: CallTask) => Promise<void>;
  onDial: (task: CallTask) => Promise<void>;
  onDialWithoutTask: (task: CallTask) => Promise<void>;
  onInvalidNumberClick: (task: CallTask, number: string) => Promise<void>;
  onSkip: () => Promise<void>;
  onStop: () => Promise<void>;
}> = ({
  isConnected,
  isDialing,
  isOpen,
  onCallNextClick,
  onDial,
  onDialWithoutTask,
  onInvalidNumberClick,
  onSkip,
  onStop,
}) => {
  const { detailOpen: visible, detailTask } = useSelector(
    (state: AppState) => state.outbound,
  );
  const dispositions = useAppSelector(selectCallDispositions);

  const [submitted, setSubmitted] = useState<string | null | undefined>();
  const [dispositionModalVisible, setDispositionModalVisible] = useState(false);
  const dispatch = useDispatch();
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const close = () => {
    if (
      CompletedCallTaskStates.includes(detailTask?.status as CallTaskStatus) &&
      detailTask?.callSid !== submitted
    ) {
      setDispositionModalVisible(true);
      return;
    }
    dispatch(setDetailOpen(false));
  };

  useEffect(() => {
    return () => {
      setSubmitted(null);
    };
  }, []);

  return (
    <>
      <Drawer
        className="ConnectedCallDetail"
        visible={!!visible}
        onClose={close}
        destroyOnClose
      >
        <ConnectedCallDetailHeader
          {...{
            isConnected,
            isDialing,
            isOpen,
            onCallNextClick,
            onDialWithoutTask,
            onInvalidNumberClick,
            onDial,
            onStop,
            onSkip,
            close,
          }}
        />
        <div className="ConnectedCallDetail__body">
          <div className="ConnectedCallDetail__side">
            <ConnectedCallDetailProfile />
          </div>
          <div className="ConnectedCallDetail__main">
            <Tabs className="ConnectedCallDetail__tabs">
              {detailTask?.status !== CallTaskStatus.PENDING && dispositions.length && (
                <Tabs.TabPane key={'actions'} tab={'Actions'}>
                  <ConnectedCallLog
                    submitted={submitted}
                    setSubmitted={setSubmitted}
                    setRefreshKey={setRefreshKey}
                  />
                </Tabs.TabPane>
              )}
              <Tabs.TabPane key={'activity'} tab={'Activity'}>
                <ConnectedCallDetailActivity refreshKey={refreshKey} />
              </Tabs.TabPane>
              <Tabs.TabPane key={'transcript'} tab={'Transcript'}>
                <ConnectedCallDetailTranscript />
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
        <ConnectedCallDetailFooter onDialWithoutTask={onDialWithoutTask} />
      </Drawer>
      <DispositionModal
        visible={dispositionModalVisible}
        close={() => setDispositionModalVisible(false)}
        setVisible={() => {
          setDispositionModalVisible(false);
          dispatch(setDetailOpen(false));
        }}
      />
    </>
  );
};
